import _get from "lodash/get"
import _isEmpty from "lodash/isEmpty"

import { aemAxiosInstance } from "@/constants/api"
import {
  GLOBAL_CONFIGURATION_ENDPOINT,
  COMPONENT_TYPES,
  PDP_TEASER_DETAILS,
} from "@/constants/index"
import {
  getSanitizedFloatingNumber,
  recursiveDataFunction,
} from "@/utils/index"
import { appendPresetToUrl, getPresetThumbnailUrl } from "@/utils/helper"
import { store } from "@/store/index"
import { setIsTeaserLoaded, setTeaserContent } from "@/store/features/pdpSlice"
import { addBuyBtnAnalytics } from "@/components/ProductDetail/v3/analytics"

const PDP_LOADER_DESKTOP_WIDTH = 1024

const normalizePdpImageUrl = imageUrl =>
  imageUrl ? imageUrl.replace(/^http:\/\//i, "https://") : ""

const getPdpImageAssetId = (imageUrl = "", assetAccountName = "PAWEB") => {
  if (!imageUrl) return ""
  const normalizedImageUrl = normalizePdpImageUrl(imageUrl).split("?")[0]
  const accountMarker = `${assetAccountName}/`
  const markerIndex = normalizedImageUrl.indexOf(accountMarker)

  if (markerIndex >= 0) {
    return normalizedImageUrl.slice(markerIndex + accountMarker.length)
  }

  return /^https?:\/\//i.test(normalizedImageUrl) ? "" : normalizedImageUrl
}

export const getPdpLoaderPresetImageUrl = ({
  assetAccountName = "PAWEB",
  swatchUrl = "",
  assetId = "",
  width = 0,
  presetConfigObject = {},
}) => {
  if (!swatchUrl || !assetId || !presetConfigObject?.LandscapeCategory) {
    return ""
  }

  return appendPresetToUrl(
    assetAccountName,
    swatchUrl,
    assetId,
    width > 0 ? width : PDP_LOADER_DESKTOP_WIDTH,
    presetConfigObject,
    true
  )
}

export const getPdpLoaderThumbs = ({
  productInfo = {},
  skuId = "",
  initImage = "",
  initImageAlt = "",
  assetAccountName = "PAWEB",
  swatchUrl = "",
  presetConfigObject = {},
  width = 0,
}) => {
  const rawVariantImages = productInfo?.["productImages.labelWithUrl_ss"] || []
  const allVariantSkus = productInfo?.["variantList.sku_ss"] || []
  const selectedImgs = []
  const sharedImgs = []

  const toLoaderImage = ({ label = "", src = "" }) => {
    const assetId = getPdpImageAssetId(src, assetAccountName)
    const presetSrc =
      swatchUrl &&
      assetId &&
      presetConfigObject?.LandscapeCategory &&
      presetConfigObject?.Thumbnail
        ? getPresetThumbnailUrl(
            assetAccountName,
            swatchUrl,
            assetId,
            presetConfigObject
          )
        : getPdpLoaderPresetImageUrl({
            assetAccountName,
            swatchUrl,
            assetId,
            width,
            presetConfigObject,
          })

    return {
      assetId,
      label,
      src: presetSrc || normalizePdpImageUrl(src),
    }
  }

  rawVariantImages.forEach(entry => {
    const parts = entry.split("|")
    const entrySku = parts[0] || ""
    const label = parts[4] || ""
    const src = parts[parts.length - 1] || ""

    if (!src || /(youtube\.com|youtu\.be|vimeo\.com)/i.test(src)) return

    const image = toLoaderImage({ label, src })

    if (entrySku === skuId) {
      selectedImgs.push(image)
    } else if (!allVariantSkus.includes(entrySku)) {
      sharedImgs.push(image)
    }
  })

  let loaderThumbs = [...selectedImgs, ...sharedImgs].filter(
    (img, index, list) =>
      list.findIndex(listImg => listImg.src === img.src) === index
  )

  if (loaderThumbs.length === 0) {
    loaderThumbs = rawVariantImages
      .map(entry => {
        const parts = entry.split("|")
        return toLoaderImage({
          label: parts[4] || "",
          src: parts[parts.length - 1] || "",
        })
      })
      .filter(
        img => img.src && !/(youtube\.com|youtu\.be|vimeo\.com)/i.test(img.src)
      )
      .filter(
        (img, index, list) =>
          list.findIndex(listImg => listImg.src === img.src) === index
      )
  }

  return loaderThumbs.length === 0 && initImage
    ? [
        toLoaderImage({
          label: initImageAlt,
          src: initImage,
        }),
      ]
    : loaderThumbs
}

export const getInitialSku = data => {
  const { productData, setSkuVariantCb, selectedColor } = data
  let colors = []
  if (productData["Color.SKU.Details_ss"].length) {
    colors = productData["Color.SKU.Details_ss"].map(color => {
      const d = color.split("|")
      return {
        color: d.at(0),
        sku: d[2],
        discontinued: !(d[4] === "false" || Date.parse(d[4]) > Date.now()),
      }
    })
  }
  const availableSkus = colors.filter(color => !color.discontinued)
  const discontinuedAvailableSkus = colors.filter(color => color.discontinued)

  let initSku = ""
  if (availableSkus[0]?.sku) {
    const { sku: selectedColorSku = "" } =
      availableSkus.find(el => el.color === selectedColor) ?? {}
    initSku = selectedColorSku
      ? selectedColorSku
      : availableSkus[0]?.sku ?? productData["masterSKU_s"]
  } else {
    initSku =
      discontinuedAvailableSkus[0]?.sku ?? productData["variantList.sku_ss"]
        ? productData["variantList.sku_ss"][0]
        : ""
  }

  setSkuVariantCb && setSkuVariantCb(initSku)

  return initSku
}

export const checkProductCategory = (productCategory, category) => {
  if (productCategory === category || productCategory === "") {
    return true
  }
  return false
}

export const getZipCodeJsonFile = (
  jsonFileUrl,
  getZipCodeJsonFileCb,
  logger = () => {}
) => {
  if (!_isEmpty(jsonFileUrl)) {
    aemAxiosInstance({
      url: GLOBAL_CONFIGURATION_ENDPOINT,
      params: {
        path: jsonFileUrl,
      },
    })
      .then(response => {
        if (response && response.data) {
          getZipCodeJsonFileCb && getZipCodeJsonFileCb(response.data)
        } else {
          logger.error(
            "Failed to fetch zip code data from provided JSON file URL",
            { url: jsonFileUrl }
          )
        }
      })
      .catch(error => {
        logger.error("Error fetching zip code JSON file", {
          error,
          url: jsonFileUrl,
        })
      })
  } else {
    logger.warn(
      "Zip code JSON file URL is missing or not provided. Cannot fetch data."
    )
  }
}

export const getShippingLocations = (statesData, states) => {
  let resultTxt = ""
  if (statesData && states?.length > 0) {
    states.map((state, i) => {
      const stateObj = statesData.find(
        st => state === st.value?.replace(/:US/g, "")
      )
      if (stateObj?.text) {
        resultTxt = resultTxt + stateObj.text
        if (i === states.length - 2) resultTxt += " and "
        else if (i !== states.length - 1) resultTxt += ", "
      }
    })
  }
  return resultTxt
}

export const getRequireItems = (
  items,
  discountedPrice,
  price,
  setInstallReqCb,
  setSubTotalCb,
  setPriceListCb,
  addItemsToCartCb
) => {
  setInstallReqCb(items)
  const cartItemProduct = []
  const priceListItems = []
  let requiredItemsPrice = 0
  let priceLists = 0
  let baseTotal = 0
  let subTotalItem = 0
  if (items.length > 0) {
    items &&
      items.map(list => {
        const product = {}
        product.sku = list.skuid
        product.brand = list.brandName
        if (list.backorder) {
          product.quantity = 1
          product.backorder = true
          cartItemProduct.push(product)
        } else {
          const checkQuantity = _get(
            list?.colorsData?.find(
              requiredItem => requiredItem.id === list.skuid
            ),
            "isOutOfStock",
            false
          )
          product.quantity = !checkQuantity ? 1 : 0
          if (!checkQuantity) {
            cartItemProduct.push(product)
          }
        }
        if (list?.priceData) {
          priceLists =
            _get(
              list?.priceData?.find(
                requiredList => requiredList.id === list.skuid
              ),
              "discountedPrice",
              0
            ) > 0
              ? _get(
                  list?.priceData?.find(
                    requiredList => requiredList.id === list.skuid
                  ),
                  "discountedPrice",
                  0
                )
              : _get(
                  list?.priceData?.find(
                    requiredList => requiredList.id === list.skuid
                  ),
                  "price",
                  0
                )

          priceListItems.push(priceLists)
          if (priceListItems.length >= 0) {
            requiredItemsPrice = priceListItems.reduce((acc, item) => {
              return parseFloat(acc) + parseFloat(item)
            }, 0)
          } else {
            requiredItemsPrice = priceListItems[0]
          }
          baseTotal = discountedPrice > 0 ? discountedPrice : price
          subTotalItem = baseTotal + requiredItemsPrice
          setSubTotalCb(subTotalItem)
          setPriceListCb(requiredItemsPrice)
        }
      })
    addItemsToCartCb(cartItemProduct, false)
  } else {
    addItemsToCartCb([], false)
  }
}

export const getTeaserFragments = (pageData, pdpData) => {
  const {
    masterSKU_s: masterSku = "",
    ProductLocalCategory_s: productLocalCategory = "",
    BundleID_s: bundleSku = "",
    BundleLocalCategory_s: bundleLocalCategory = "",
  } = pdpData

  const teaserFragmentOrder = getFragmentsOrder(pageData)
  const customerFacingSKU = masterSku || bundleSku
  const category = productLocalCategory || bundleLocalCategory
  const pdpXFPaths = getFragmentPaths(pageData)

  if (teaserFragmentOrder?.length) {
    store.dispatch(setIsTeaserLoaded(true))
    const params = {
      skuId: customerFacingSKU,
      category: category,
    }
    if (pdpXFPaths) params.xfPaths = pdpXFPaths
    aemAxiosInstance({
      url: PDP_TEASER_DETAILS,
      params,
    })
      .then(response => {
        const { data } = response
        if (data && teaserFragmentOrder.length)
          getTeaserDetails(data, teaserFragmentOrder)
        else console.error("PDP Teaser Fragments Order Not Found..!")
      })
      .catch(error => {
        console.error("PDP Teaser Fragments Fetching Error..!")
      })
  }
}

const getFragmentsOrder = pageData => {
  const itemOrder = []
  const teaserFragments = recursiveDataFunction(
    pageData,
    COMPONENT_TYPES.PRODUCT_DETAILS_EXFPDP
  )
  if (teaserFragments && teaserFragments.length) {
    const list = _get(teaserFragments[0], "configuredXfPathModels", "")
    list.map(item => {
      const itemArray = item && item?.path && item.path.split("/")
      itemOrder.push(itemArray[itemArray.length - 1])
    })
    return itemOrder
  }
}

const getFragmentPaths = pageData => {
  const teaserFragments = recursiveDataFunction(
    pageData,
    COMPONENT_TYPES.PRODUCT_DETAILS_EXFPDP
  )
  if (teaserFragments && teaserFragments.length) {
    const fragmentPaths = teaserFragments.reduce((paths, fragment) => {
      const list = _get(fragment, "configuredXfPathModels", "")
      list.forEach(item => {
        paths.push(item?.path)
      })
      return paths
    }, [])
    return fragmentPaths.join(",")
  }

  return ""
}

export const getTeaserDetails = (data, teaserFragmentOrder) => {
  const teaserDataPromises = []
  teaserFragmentOrder.forEach(async item => {
    if (data[item]) {
      const teaserDeta = data[item].map(async url => {
        const resData = await aemAxiosInstance({
          url: GLOBAL_CONFIGURATION_ENDPOINT,
          params: {
            path: `${url}.model.json`,
          },
        })
        return resData.data
      })

      teaserDataPromises.push(...teaserDeta)
    }
  })

  Promise.all(teaserDataPromises)
    .then(response => {
      store.dispatch(setTeaserContent(response))
    })
    .catch(error => {
      store.dispatch(setTeaserContent([]))
      console.error("PDP Teaser Fragments Data Fetching Error..!")
    })
}

export const initPriceSpider = productInfo => {
  const waitTimeForPsWidgetElementsToLoad = 500
  let psWidgetNode = document.getElementsByClassName("ps-widget")[0]

  // Here reason to use mutation observer instead of react ref is bcoz
  // this ps widget elements are getting fluctuated
  const psWidgetObserver = new MutationObserver(() => {
    // fired when a mutation occurs
    psWidgetNode.querySelectorAll("div[data-retailer]").forEach(retailerDiv => {
      const btn = retailerDiv.querySelector(".ps-online-buy-button")
      const retailer = retailerDiv.getAttribute("data-retailer")?.toLowerCase()
      const priceText =
        retailerDiv.querySelector(".ps-price")?.textContent || "n/a"
      const price = parseFloat(getSanitizedFloatingNumber(priceText)) || "n/a"
      if (btn.getAttribute("attachedEvent") !== "true") {
        btn.addEventListener(
          "click",
          e => {
            addBuyBtnAnalytics(e, retailer, productInfo, price)
          },
          false
        )
        btn.setAttribute("attachedEvent", "true")
      }
    })
  })

  let psWidgetObserverTimeout
  const addObserverIfDesiredNodeAvailable = () => {
    psWidgetNode = document.getElementsByClassName("ps-widget")[0]
    if (!psWidgetNode) {
      psWidgetObserverTimeout = setTimeout(
        addObserverIfDesiredNodeAvailable,
        waitTimeForPsWidgetElementsToLoad
      )
      return
    }

    if (psWidgetObserverTimeout) {
      clearTimeout(psWidgetObserverTimeout)
      psWidgetObserverTimeout = undefined
    }

    const config = { subtree: true, childList: true }
    psWidgetObserver.observe(psWidgetNode, config)
  }

  addObserverIfDesiredNodeAvailable()
}

export const getDataSheetUrl = (productServices = [], currentSku = "") => {
  const currentSkuMasterVariant =
    productServices.find(
      product => product?.masterVariant?.sku === currentSku
    ) ?? {}

  const { masterVariant: { attributes = [] } = {} } = currentSkuMasterVariant

  const { value: productResourceData = [] } =
    attributes.find(e => e.name === "ProductResource") ?? {}

  const dataSheet =
    productResourceData.find(e => {
      return e.find(el => el.value === "DataSheet")
    }) ?? []

  const { value: resourceFullWebURL = "" } =
    dataSheet.find(e => e.name === "ResourceFullWebURL") ?? {}

  return resourceFullWebURL
}

export const getColorDataObject = (color = "") => {
  const d = color.split("|")
  return {
    name: d.at(0),
    swatch: d.at(1),
    sku: d.at(2),
    isStock: d.at(3),
    discontinued: !(d.at(4) === "false" || Date.parse(d.at(4)) > Date.now()),
    customerSku: d.at(5),
    slug: d.at(6),
  }
}

const getVariantOptionValue = (option = {}, field = "") => {
  const value = option?.[field]
  if (Array.isArray(value)) {
    return value[0] ?? ""
  }

  return value ?? ""
}

export const findVariantOptionBySku = (variantOptions = [], sku = "") => {
  return (
    variantOptions.find(option => {
      const variantList = option?.["variantList.sku_ss"] || []

      return option?.sku_s === sku || variantList.includes(sku)
    }) ?? {}
  )
}

export const resolveCollapsedPdpColorSelection = (data = {}) => {
  const {
    variantData = {},
    variantOptions = [],
    currOptions = {},
    options = {},
  } = data
  const {
    value: fallbackSku = "",
    slug: fallbackSlug = "",
    skus = [],
  } = variantData
  const candidateSkus = [...new Set([fallbackSku, ...skus].filter(Boolean))]
  const selectedOptionFields = Object.keys(options).filter(field => {
    return getVariantOptionValue(currOptions, field) !== ""
  })

  const matchingSku =
    selectedOptionFields.length > 0
      ? candidateSkus.find(candidateSku => {
          const option = findVariantOptionBySku(variantOptions, candidateSku)

          return (
            !_isEmpty(option) &&
            selectedOptionFields.every(field => {
              return (
                getVariantOptionValue(option, field) ===
                getVariantOptionValue(currOptions, field)
              )
            })
          )
        })
      : ""

  const resolvedSku = matchingSku || candidateSkus[0] || fallbackSku
  const resolvedOption = findVariantOptionBySku(variantOptions, resolvedSku)

  return {
    sku: resolvedSku,
    slug: resolvedOption?.slug_s || fallbackSlug,
    currOptions: resolvedOption,
  }
}

export const getColorFinishData = (data = {}) => {
  return data["Color.SKU.Details_ss"] || []
}

export const checkUserZipCode = data => {
  const { userZipCode, state, callBack, productServices } = data
  let availableSkuIdsForService = []
  const { zipCodeData = [] } = state
  for (const zipKey of zipCodeData) {
    const key = Object.keys(zipKey)[0]
    for (const zipInnerKey of zipKey[key]) {
      const zipcode = Object.keys(zipInnerKey)[0]
      if (zipcode === userZipCode) {
        if (
          productServices.length > 0 &&
          zipInnerKey[zipcode].includes(productServices[0])
        ) {
          availableSkuIdsForService = zipInnerKey[zipcode]
        }
        break
      }
    }
    if (availableSkuIdsForService.length) break
  }
  const isNearest = availableSkuIdsForService.length
  callBack && callBack(isNearest)
}
